import { useEffect, useState } from 'react';
import { Wallet, Landmark, Clock3, TrendingUp, Loader2, ArrowDownToLine, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { studioGetEarnings, studioUpdatePayoutMethod, studioRequestPayout, apiErrorMessage } from '@/lib/api';
import { formatCents } from '@/lib/money';
import { ADMIN_EARNINGS as T } from '@/constants/testIds';

const fieldClass = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10 focus-visible:ring-[hsl(var(--lxra-gold))]';
const labelClass = 'text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]';
const EMPTY_METHOD = { holder_name: '', bank_name: '', account_number: '', routing_number: '' };

const StatCard = ({ icon: Icon, label, value, hint, testId, accent }) => (
  <div data-testid={testId} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5">
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]"><Icon className="h-3.5 w-3.5" /> {label}</div>
    <p className={`font-display text-3xl mt-2 ${accent ? 'text-[hsl(var(--lxra-gold))]' : 'text-[hsl(var(--lxra-fg))]'}`}>{value}</p>
    {hint && <p className="text-[11px] text-[hsl(var(--lxra-muted))] mt-1">{hint}</p>}
  </div>
);

/** Photographer-facing wallet: available/pending balance, lifetime LXRA fees paid,
 * a simulated bank payout method, and a withdraw button. Fully simulated — no
 * payment processor is wired up; see routers/payments.py for the ledger logic. */
export const AdminEarningsTab = () => {
  const [data, setData] = useState(null);
  const [method, setMethod] = useState(EMPTY_METHOD);
  const [savingMethod, setSavingMethod] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  const refresh = () => studioGetEarnings().then(setData).catch(() => toast.error('Could not load your earnings.'));

  useEffect(() => { refresh(); }, []);

  const saveMethod = async (e) => {
    e.preventDefault();
    setSavingMethod(true);
    try {
      const saved = await studioUpdatePayoutMethod(method);
      setData((cur) => ({ ...cur, payout_method: saved }));
      setMethod(EMPTY_METHOD);
      toast.success('Payout bank account saved.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save your payout method.'));
    } finally {
      setSavingMethod(false);
    }
  };

  const withdraw = async () => {
    setWithdrawing(true);
    try {
      const updated = await studioRequestPayout();
      setData(updated);
      toast.success(`Withdrew ${formatCents(updated.lifetime_paid_out_cents - (data?.lifetime_paid_out_cents || 0))} to your bank (demo).`);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not process the payout.'));
    } finally {
      setWithdrawing(false);
    }
  };

  if (!data) return <Skeleton className="h-96 rounded-2xl bg-[#141416]" />;

  const isMarketplace = data.commission_percent > 0;

  return (
    <div className="space-y-6" data-testid={T.section}>
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">LXRA Marketplace</p>
        <h2 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">Earnings</h2>
        <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">
          {isMarketplace
            ? `LXRA takes a ${data.commission_percent}% fee on client payments; the rest is yours. Funds unlock once a session is marked "delivered."`
            : 'Direct LXRA Studios bookings keep 100% — the marketplace fee only applies to independent photographers.'}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Wallet} label="Available now" value={formatCents(data.available_cents)} hint="Ready to withdraw" testId={T.available} accent />
        <StatCard icon={Clock3} label="Pending" value={formatCents(data.pending_cents)} hint="Unlocks at delivery" testId={T.pending} />
        <StatCard icon={TrendingUp} label="Lifetime net" value={formatCents(data.lifetime_net_cents)} hint={`Of ${formatCents(data.lifetime_gross_cents)} collected`} testId={T.lifetimeNet} />
        <StatCard icon={Landmark} label="LXRA fees paid" value={formatCents(data.lifetime_fee_cents)} hint="Lifetime marketplace commission" testId={T.lifetimeFee} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg tracking-[0.04em] text-[hsl(var(--lxra-fg))]">Payout bank account</h3>
            <Button
              type="button"
              onClick={withdraw}
              disabled={withdrawing || data.available_cents <= 0 || !data.payout_method}
              data-testid={T.withdrawButton}
              className="h-9 px-4 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold disabled:opacity-40"
            >
              {withdrawing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowDownToLine className="h-4 w-4 mr-1.5" /> Withdraw {formatCents(data.available_cents)}</>}
            </Button>
          </div>
          {data.payout_method ? (
            <div className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] p-4 text-sm text-[hsl(var(--lxra-fg))]">
              <p>{data.payout_method.bank_name} · Account •••• {data.payout_method.account_last4}</p>
              <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">{data.payout_method.holder_name} · Routing •••• {data.payout_method.routing_last4}</p>
            </div>
          ) : (
            <p className="text-xs text-[hsl(var(--lxra-muted))] flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> Add a bank account below to enable withdrawals.</p>
          )}
          <form onSubmit={saveMethod} data-testid={T.payoutMethodForm} className="space-y-3 pt-2 border-t border-[hsl(var(--lxra-border))]">
            <p className="text-[11px] text-[hsl(var(--lxra-muted))] pt-3">{data.payout_method ? 'Update bank account' : 'Add bank account'} <span className="text-[hsl(var(--lxra-gold))]">(demo — not a real bank connection)</span></p>
            <div className="space-y-1.5">
              <Label className={labelClass}>Account holder name</Label>
              <Input required value={method.holder_name} onChange={(e) => setMethod({ ...method, holder_name: e.target.value })} data-testid={T.holderNameInput} className={fieldClass} placeholder="Maya Chen" />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Bank name</Label>
              <Input required value={method.bank_name} onChange={(e) => setMethod({ ...method, bank_name: e.target.value })} data-testid={T.bankNameInput} className={fieldClass} placeholder="Chase" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className={labelClass}>Account number</Label>
                <Input required inputMode="numeric" value={method.account_number} onChange={(e) => setMethod({ ...method, account_number: e.target.value })} data-testid={T.accountNumberInput} className={fieldClass} placeholder="000123456789" />
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>Routing number</Label>
                <Input required inputMode="numeric" value={method.routing_number} onChange={(e) => setMethod({ ...method, routing_number: e.target.value })} data-testid={T.routingNumberInput} className={fieldClass} placeholder="021000021" />
              </div>
            </div>
            <Button type="submit" disabled={savingMethod} data-testid={T.savePayoutMethodButton} className="h-9 px-4 rounded-lg bg-transparent border border-[hsl(var(--lxra-gold))] text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))]/10 text-xs uppercase tracking-[0.1em] font-semibold">
              {savingMethod ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save bank account'}
            </Button>
          </form>
        </div>

        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-6">
          <h3 className="font-display text-lg tracking-[0.04em] text-[hsl(var(--lxra-fg))] mb-4">Payout history</h3>
          {data.payouts.length === 0 ? (
            <p data-testid={T.empty} className="text-sm text-[hsl(var(--lxra-muted))]">No payouts yet.</p>
          ) : (
            <ul data-testid={T.payoutHistory} className="space-y-3">
              {data.payouts.map((p) => (
                <li key={p.id} data-testid={T.payoutRow(p.id)} className="flex items-center justify-between border-t border-[hsl(var(--lxra-border))] pt-3 first:border-0 first:pt-0 text-sm">
                  <span className="text-[hsl(var(--lxra-fg))]">{formatCents(p.amount_cents)}</span>
                  <span className="text-[hsl(var(--lxra-muted))] text-xs">{new Date(p.created_at).toLocaleDateString()} · {p.bank_snapshot?.bank_name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
